import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChartBarIcon,
  PencilIcon,
  PlusCircleIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  XCircleIcon,
  CloudArrowUpIcon,
  CloudArrowDownIcon,
} from '@heroicons/react/24/outline';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../App';
import { schedulePersistClientSettings } from '../context/userSettingsSync';
import { computeHeaderPrice } from '../utils/premarketQuote';
import { openStockInNewTab } from '../utils/openStock';

// Default portfolio for new users or when no data exists
const DEFAULT_PORTFOLIO = [
  {
    id: 'list-1',
    name: 'My Core Holdings',
    type: 'portfolio', // 'portfolio' or 'watchlist'
    stocks: [
      {
        id: 'stock-1',
        symbol: 'AAPL',
        market: 'NASDAQ',
        shares: 50,
        avgBuyPrice: 150.75,
      },
      {
        id: 'stock-2',
        symbol: 'GOOGL',
        market: 'NASDAQ',
        shares: 20,
        avgBuyPrice: 2800.50,
      },
    ],
  },
  {
    id: 'list-2',
    name: 'Speculative Plays',
    type: 'portfolio',
    stocks: [
      {
        id: 'stock-3',
        symbol: 'TSLA',
        market: 'NASDAQ',
        shares: 10,
        avgBuyPrice: 700.00,
      },
    ],
  },
  {
    id: 'list-3',
    name: 'Watchlist',
    type: 'watchlist',
    stocks: [
      {
        id: 'stock-4',
        symbol: 'NVDA',
        market: 'NASDAQ',
      },
      {
        id: 'stock-5',
        symbol: 'AMZN',
        market: 'NASDAQ',
      },
    ],
  },
];

// Minimal fallback used when local portfolioData cannot be read.
// Keeps the app usable without overwriting any existing localStorage unexpectedly.
const DEFAULT_EMPTY_LISTS = [
  {
    id: 'list-watchlist',
    name: 'My watchlist',
    type: 'watchlist',
    stocks: [],
  },
  {
    id: 'list-portfolio',
    name: 'My portfolio',
    type: 'portfolio',
    stocks: [],
  },
];

// Context menu for right-click options
const ContextMenu = ({ x, y, onClose, symbol, market }) => {
  const menuRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const stockUrl = `/summary?stock=${symbol}&market=${market}`;

  const handleOpenNewTab = () => {
    window.open(stockUrl, '_blank');
    onClose();
  };

  const handleOpenNewWindow = () => {
    window.open(stockUrl, '_blank', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no');
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[160px]"
      style={{ left: x, top: y }}
    >
      <button
        onClick={handleOpenNewTab}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        Open in New Tab
      </button>
      <button
        onClick={handleOpenNewWindow}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        Open in New Window
      </button>
    </div>
  );
};

// A simple placeholder for the small chart
const MiniChart = ({ symbol, market, avgBuyPrice, lastPrice }) => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['historical', symbol],
        queryFn: async () => {
            // Fetch last 90 days of historical data
            const response = await client.get(`/market/historical/${symbol}?market=${market}`);
            return response.data;
        },
        staleTime: 1000 * 60 * 60, // Cache for 1 hour
    });

    const theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    const placeholderColor = theme === 'dark' ? '#4A5568' : '#E2E8F0';

    if (isLoading) {
        return <div className="w-24 h-10 flex items-center justify-center text-xs text-gray-500">Loading...</div>;
    }

    if (error || !data || data.length < 2) {
        return <div className="w-24 h-10 rounded-md" style={{ backgroundColor: placeholderColor }}></div>;
    }

    // Ensure we only use the last 90 data points
    const chartData = data.slice(-90).map(d => d.close);

    if (chartData.length < 2) {
        return <div className="w-24 h-10 rounded-md" style={{ backgroundColor: placeholderColor }}></div>;
    }

    const width = 96; // w-24
    const height = 40; // h-10
    const padding = 2;

    // Include avgBuyPrice in the range for correct scaling (if provided)
    const hasAvgBuyPrice = avgBuyPrice !== undefined && avgBuyPrice !== null;
    const minPrice = hasAvgBuyPrice ? Math.min(...chartData, avgBuyPrice) : Math.min(...chartData);
    const maxPrice = hasAvgBuyPrice ? Math.max(...chartData, avgBuyPrice) : Math.max(...chartData);
    const priceRange = maxPrice - minPrice;

    // Create SVG path points
    const points = chartData
        .map((price, index) => {
            const x = (index / (chartData.length - 1)) * (width - padding * 2) + padding;
            const y = height - ((price - minPrice) / (priceRange || 1)) * (height - padding * 2) - padding;
            return `${x},${y}`;
        })
        .join(' ');

    // Calculate Y position for the average buy price line (only if provided)
    const avgBuyPriceY = hasAvgBuyPrice 
        ? height - ((avgBuyPrice - minPrice) / (priceRange || 1)) * (height - padding * 2) - padding
        : null;

    // Determine trend color based on last price vs first price (or avg buy price if provided)
    const referencePrice = hasAvgBuyPrice ? avgBuyPrice : chartData[0];
    const trendColor = lastPrice >= referencePrice ? '#10B981' : '#EF4444'; // green-500 or red-500

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="rounded-md">
            <polyline
                fill="none"
                stroke={trendColor}
                strokeWidth="1.5"
                points={points}
            />
            {avgBuyPriceY !== null && (
                <line
                    x1={padding}
                    y1={avgBuyPriceY}
                    x2={width - padding}
                    y2={avgBuyPriceY}
                    stroke={theme === 'dark' ? '#A0AEC0' : '#718096'} // gray-400 or gray-500
                    strokeWidth="1"
                    strokeDasharray="2,2"
                />
            )}
        </svg>
    );
};


// Modal for adding a new list
const AddListModal = ({ isOpen, onClose, onAddList, theme, defaultType = 'watchlist' }) => {
  const [listName, setListName] = useState('');
  const [listType, setListType] = useState(defaultType);

  useEffect(() => {
    if (isOpen) {
      setListName('');
      setListType(defaultType);
    }
  }, [isOpen, defaultType]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!listName.trim()) {
      alert('Please enter a list name.');
      return;
    }
    onAddList(listName.trim(), listType);
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div 
        className={`p-6 rounded-lg shadow-xl w-full max-w-md ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">Create New List</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="listType" className="block text-sm font-medium mb-1">List Type</label>
            <select
              id="listType"
              value={listType}
              onChange={(e) => setListType(e.target.value)}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'border-gray-300 bg-white'}`}
            >
              <option value="watchlist">Watchlist (symbols only)</option>
              <option value="portfolio">Portfolio (with shares & buy price)</option>
            </select>
          </div>
          <div>
            <label htmlFor="listName" className="block text-sm font-medium mb-1">List Name</label>
            <input
              id="listName"
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'border-gray-300'}`}
              placeholder={listType === 'watchlist' ? 'e.g., Tech Stocks to Watch' : 'e.g., My Growth Portfolio'}
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-sm font-medium rounded-md ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Create List
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const AddStockModal = ({ isOpen, onClose, onAddStock, onEditStock, editingStock, theme, listType = 'portfolio' }) => {
  const [symbol, setSymbol] = useState('');
  const [originalSymbol, setOriginalSymbol] = useState('');
  const [shares, setShares] = useState('');
  const [avgBuyPrice, setAvgBuyPrice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [symbolQuery, setSymbolQuery] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const symbolInputRef = useRef(null);

  const isEditMode = !!editingStock;

  useEffect(() => {
    if (isEditMode) {
      const sym = (editingStock.symbol || '').toUpperCase();
      setOriginalSymbol(sym);
      setSymbol(sym);
      setSelectedSymbol(sym);
      setSelectedMarket(editingStock.market || '');
      setShares(editingStock.shares?.toString() || '');
      setAvgBuyPrice(editingStock.avgBuyPrice?.toString() || '');
      setError(''); // Clear any previous errors
      setSymbolQuery('');
    } else {
      // Reset form for adding new stock
      setSymbol('');
      setOriginalSymbol('');
      setSelectedSymbol('');
      setSelectedMarket('');
      setShares('');
      setAvgBuyPrice('');
      setSymbolQuery('');
    }
    setActiveIndex(0);
    // Focus the symbol input when modal opens
    if (isOpen && symbolInputRef.current) {
      setTimeout(() => {
        try { symbolInputRef.current.focus(); } catch {}
      }, 0);
    }
  }, [editingStock, isOpen]); // Rerun when modal opens or editingStock changes

  // Fetch all symbols for validation
  const { data: allSymbols, isLoading: symbolsLoading } = useQuery({
    queryKey: ['allSymbols'],
    queryFn: async () => {
      const response = await client.get('/symbols/all');
      // Assuming the response is an array of strings: ['AAPL', 'GOOGL', ...]
      return new Set(response.data.map(s => s.toUpperCase()));
    },
    staleTime: Infinity, // This data is static, so no need to refetch
  });

  // Search stocks by symbol OR name (same behavior as sidebar filter)
  const canSearch = !selectedSymbol && symbolQuery.trim().length >= 2;
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['marketSearch', symbolQuery.trim()],
    enabled: !!isOpen && canSearch,
    queryFn: async () => {
      const q = symbolQuery.trim();
      const response = await client.get(`/market/search?q=${encodeURIComponent(q)}&limit=25`);
      const rows = Array.isArray(response.data) ? response.data : [];
      return rows
        .map((r) => ({
          symbol: (r.symbol || '').toUpperCase(),
          name: (r.name || '').toString(),
          market: (r.market || '').toString(),
        }))
        .filter((r) => !!r.symbol);
    },
    staleTime: 1000 * 60 * 5,
  });

  const normalizedResults = useMemo(() => {
    const rows = Array.isArray(searchResults) ? searchResults : [];
    const unique = new Map();
    for (const r of rows) {
      const key = `${r.market || ''}::${r.symbol}`;
      if (!unique.has(key)) unique.set(key, r);
    }
    return Array.from(unique.values());
  }, [searchResults]);

  useEffect(() => {
    setActiveIndex(0);
  }, [symbolQuery]);

  if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const isPortfolio = listType === 'portfolio';
        if (!symbol || (isPortfolio && (!shares || !avgBuyPrice))) {
            alert(isPortfolio ? 'Please fill out all fields.' : 'Please enter a stock symbol.');
            return;
        }

        if (!allSymbols) {
            setError('This stock does not exist in our system. Please contact support to request to add this stock.');
            return;
        }

        const upperSymbol = symbol.toUpperCase();

        if (!allSymbols.has(upperSymbol)) {
            setError(`We do not provide information for the stock symbol "${upperSymbol}". Please enter a valid symbol.`);
            return;
        }

        setLoading(true);
        try {
            // Always include market info
            let marketValue = selectedMarket || '';
            // If editing, prefer the market from the editingStock if missing
            if (!marketValue && isEditMode && editingStock?.market) {
                marketValue = editingStock.market;
            }

            // For watchlist, set shares: 0 and avgBuyPrice: lastPrice
            let stockData = {
                symbol: upperSymbol,
                market: marketValue,
                originalSymbol: isEditMode ? (originalSymbol || (editingStock?.symbol || '').toUpperCase()) : undefined,
            };

            if (listType === 'portfolio') {
                stockData.shares = parseFloat(shares);
                stockData.avgBuyPrice = parseFloat(avgBuyPrice);
            } else {
                // For watchlist, fetch lastPrice for avgBuyPrice
                let lastPrice = 0;
                try {
                    const response = await client.get(`/quote?symbol=${upperSymbol}&market=${marketValue || 'NASDAQ'}`);
                    lastPrice = response.data?.close ?? 0;
                } catch (err) {
                    lastPrice = 0;
                }
                stockData.shares = 0;
                stockData.avgBuyPrice = lastPrice;
            }

            if (isEditMode) {
                await onEditStock(stockData);
            } else {
                await onAddStock(stockData);
            }

            // Reset form and close on success
            onClose(); // The parent component will reset the state
        } catch (err) {
            setError(err.message || 'An error occurred.');
        } finally {
            setLoading(false);
        }
    };

  const applySelection = (sel) => {
    if (!sel || !sel.symbol) return;
    setSelectedSymbol(sel.symbol);
    setSymbol(sel.symbol);
    setSelectedMarket(sel.market || '');
    setSymbolQuery('');
    setActiveIndex(0);
    try {
      if (symbolInputRef.current) symbolInputRef.current.focus();
    } catch {}
  };

  const clearSelection = () => {
    setSelectedSymbol('');
    setSymbol('');
    setSelectedMarket('');
    setSymbolQuery('');
    setActiveIndex(0);
    setError('');
    try {
      if (symbolInputRef.current) symbolInputRef.current.focus();
    } catch {}
  };

  const handleSymbolKeyDown = (e) => {
    if (selectedSymbol) {
      // User must clear via X button; prevent partial edits.
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        return;
      }
      return;
    }
    if (!canSearch || !normalizedResults.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, normalizedResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // If the user is navigating suggestions, selecting should not submit the form.
      if (normalizedResults[activeIndex]) {
        e.preventDefault();
        applySelection(normalizedResults[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setSymbolQuery('');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div 
        className={`p-6 rounded-lg shadow-xl w-full max-w-md ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">{isEditMode ? 'Edit Stock' : (listType === 'watchlist' ? 'Add to Watchlist' : 'Add New Stock')}</h3>
        {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="symbol" className="block text-sm font-medium">Stock Symbol</label>
            <div className="relative">
              <input
                id="symbol"
                type="text"
                ref={symbolInputRef}
                value={selectedSymbol || symbolQuery}
                onChange={(e) => {
                  if (selectedSymbol) return;
                  const v = e.target.value;
                  setSymbolQuery(v);
                  // Keep `symbol` in sync so manual typing still works.
                  setSymbol(v);
                }}
                onKeyDown={handleSymbolKeyDown}
                className={`mt-1 block w-full px-3 pr-9 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'border-gray-300'} ${isEditMode ? 'bg-gray-200 dark:bg-gray-600 cursor-not-allowed' : ''} ${selectedSymbol ? 'cursor-default' : ''}`}
                placeholder={selectedSymbol ? '' : 'Type symbol or company name (min 2 chars)'}
                required
                disabled={false}
                readOnly={!!selectedSymbol}
              />
              {selectedSymbol && (
                <div className="absolute inset-y-0 right-2 flex items-center">
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="leading-none text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                    title="Clear selection"
                    aria-label="Clear selection"
                  >
                    <XMarkIcon className="h-4 w-4 block" />
                  </button>
                </div>
              )}
            </div>

            {(
              <>
              <div className="relative">
                {canSearch && (
                  <div className={`mt-2 max-h-56 overflow-auto rounded-md border shadow-sm ${theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    {searchLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
                    ) : normalizedResults.length ? (
                      normalizedResults.map((r, idx) => {
                        const isActive = idx === activeIndex;
                        return (
                          <button
                            key={`${r.market || 'UNKNOWN'}-${r.symbol}`}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${isActive ? 'bg-blue-600 text-white' : (theme === 'dark' ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-50 text-gray-800')}`}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onClick={() => applySelection(r)}
                            title={r.name || r.symbol}
                          >
                            <span className="truncate">
                              <span className="font-semibold">{r.symbol}</span>
                              {r.name ? <span className={`${isActive ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'} ml-2`}>{r.name}</span> : null}
                            </span>
                            {r.market ? (
                              <span className={`${isActive ? 'text-white/80' : 'text-gray-400'} text-xs ml-3`}>{r.market}</span>
                            ) : null}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
                    )}
                  </div>
                )}
                {!selectedSymbol && (
                  <div className="mt-1 text-xs text-gray-500">
                    Tip: Use ↑/↓ then Enter to select.
                  </div>
                )}
              </div>
              </>
            )}
          </div>
          {listType === 'portfolio' && (
            <>
              <div>
                <label htmlFor="shares" className="block text-sm font-medium">Number of Shares</label>
                <input
                  id="shares"
                  type="number"
                  value={shares}
                  onChange={(e) => setShares(e.target.value)}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'border-gray-300'}`}
                  placeholder="e.g., 50"
                  required
                />
              </div>
              <div>
                <label htmlFor="avgBuyPrice" className="block text-sm font-medium">Average Buy Price</label>
                <input
                  id="avgBuyPrice"
                  type="number"
                  step="0.01"
                  value={avgBuyPrice}
                  onChange={(e) => setAvgBuyPrice(e.target.value)}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'border-gray-300'}`}
                  placeholder="e.g., 150.75"
                  required
                />
              </div>
            </>
          )}
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-sm font-medium rounded-md ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!isEditMode && symbolsLoading)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (isEditMode ? 'Save Changes' : (symbolsLoading ? 'Validating...' : 'Add Symbol'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const PortfolioStockRow = ({ stock, listId, theme, tdCls, formatCurrency, formatChange, formatPL, handleDeleteStock, handleEditStock, navigate, onContextMenu }) => {
  const { data: quote, isLoading: quoteLoading } = useQuery({
    queryKey: ['quote', stock.symbol],
    queryFn: async () => {
      const response = await client.get(`/quote?symbol=${stock.symbol}&market=${stock.market}`);
      return response.data;
    },
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000,
  });

  // Fetch historical data to match StockDashboardDetail logic
  const { data: historical, isLoading: histLoading } = useQuery({
    queryKey: ['historical', stock.symbol],
    queryFn: async () => {
      const response = await client.get(`/market/historical/${stock.symbol}?market=${stock.market}`);
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (same as MiniChart)
  });

  const isLoading = quoteLoading || histLoading;

  // Compute lastPrice and change using the shared pre-market/stale-quote
  // helper (see utils/premarketQuote.js).  When the provider's quote is
  // stale (e.g. during pre-market it returns yesterday's regular session),
  // the latest DB candle wins, so the portfolio keeps showing the correct
  // HLOC close from the database.
  const { displayClose, pct, change } = computeHeaderPrice(quote, Array.isArray(historical) ? historical : []);
  let lastPrice = 0;
  let changePercent = 0;
  lastPrice = displayClose ?? 0;
  changePercent = pct ?? 0;
  const unrealizedPL = (lastPrice - stock.avgBuyPrice) * stock.shares;
  const market = stock.market || quote?.market || '';

  const handleRightClick = (e) => {
    e.preventDefault();
    onContextMenu(e, stock.symbol, market);
  };

  const openInNewTab = (e) => {
    e.preventDefault();
    openStockInNewTab(stock.symbol, market);
  };

  return (
    <tr className={theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}>
      <td
        className={`${tdCls} font-medium cursor-pointer text-blue-600 dark:text-blue-400 hover:underline`}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey) {
            openInNewTab(e);
            return;
          }
          navigate(`/summary?stock=${stock.symbol}&market=${market}`);
        }}
        onAuxClick={(e) => {
          if (e.button === 1) openInNewTab(e);
        }}
        onContextMenu={handleRightClick}
      >
        {stock.symbol}
      </td>
      <td className={tdCls}>{isLoading && !quote ? '...' : formatCurrency(lastPrice)}</td>
      <td className={tdCls}>{isLoading && !quote ? '...' : formatChange(change, changePercent)}</td>
      <td className={tdCls}>{stock.shares}</td>
      <td className={tdCls}>{formatCurrency(stock.avgBuyPrice)}</td>
      <td className={tdCls}>{formatPL(unrealizedPL)}</td>
      <td className={`${tdCls} flex items-center justify-center`}>
        <MiniChart 
          symbol={stock.symbol} 
          market={market} 
          avgBuyPrice={stock.avgBuyPrice}
          lastPrice={lastPrice}
        />
      </td>
      <td className={`${tdCls}`}>
        <button
          onClick={() => handleEditStock(listId, stock)}
          className="text-gray-400 hover:text-blue-500"
          title="Edit Symbol"
        >
          <PencilIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => handleDeleteStock(listId, stock.symbol)}
          className="text-gray-400 hover:text-red-500"
          title="Delete Symbol"
        >
          <XCircleIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};


const WatchlistStockRow = ({ stock, listId, theme, tdCls, formatCurrency, formatChange, handleDeleteStock, navigate, onContextMenu }) => {
  const { data: quote, isLoading: quoteLoading } = useQuery({
    queryKey: ['quote', stock.symbol],
    queryFn: async () => {
      const response = await client.get(`/quote?symbol=${stock.symbol}&market=${stock.market || 'NASDAQ'}`);
      return response.data;
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Fetch historical data to match StockDashboardDetail logic
  const { data: historical, isLoading: histLoading } = useQuery({
    queryKey: ['historical', stock.symbol],
    queryFn: async () => {
      const response = await client.get(`/market/historical/${stock.symbol}?market=${stock.market || 'NASDAQ'}`);
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (same as MiniChart)
  });

  const isLoading = quoteLoading || histLoading;

  // Compute lastPrice and change using the shared pre-market/stale-quote
  // helper (see utils/premarketQuote.js).  When the provider's quote is
  // stale (e.g. during pre-market it returns yesterday's regular session),
  // the latest DB candle wins, so the portfolio keeps showing the correct
  // HLOC close from the database.
  const { displayClose, pct, change } = computeHeaderPrice(quote, Array.isArray(historical) ? historical : []);
  let lastPrice = 0;
  let changePercent = 0;
  lastPrice = displayClose ?? 0;
  changePercent = pct ?? 0;
  const market = stock.market || quote?.market || '';

  const handleRightClick = (e) => {
    e.preventDefault();
    onContextMenu(e, stock.symbol, market);
  };

  const openInNewTab = (e) => {
    e.preventDefault();
    openStockInNewTab(stock.symbol, market);
  };

  return (
    <tr className={theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}>
      <td
        className={`${tdCls} font-medium cursor-pointer text-blue-600 dark:text-blue-400 hover:underline`}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey) {
            openInNewTab(e);
            return;
          }
          navigate(`/summary?stock=${stock.symbol}&market=${market}`);
        }}
        onAuxClick={(e) => {
          if (e.button === 1) openInNewTab(e);
        }}
        onContextMenu={handleRightClick}
      >
        {stock.symbol}
      </td>
      <td className={tdCls}>{isLoading && !quote ? '...' : formatCurrency(lastPrice)}</td>
      <td className={tdCls}>{isLoading && !quote ? '...' : formatChange(change, changePercent)}</td>
      <td className={`${tdCls} flex items-center justify-center`}>
        <MiniChart 
          symbol={stock.symbol} 
          market={market}
          avgBuyPrice={stock.avgBuyPrice}
          lastPrice={lastPrice}
        />
      </td>
      <td className={`${tdCls}`}>
        <button
          onClick={() => handleDeleteStock(listId, stock.symbol)}
          className="text-gray-400 hover:text-red-500"
          title="Remove from Watchlist"
        >
          <XCircleIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};


const Portfolio = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [portfolioLists, setPortfolioLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'syncing' | 'synced' | 'error'
  const [expandedLists, setExpandedLists] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddListModalOpen, setIsAddListModalOpen] = useState(false);
  const [renamingListId, setRenamingListId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [addingToListId, setAddingToListId] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, symbol, market }

  // Debounce timer for auto-sync
  const syncTimeoutRef = useRef(null);

  // Sync to database function
  const syncToDatabase = useCallback(async (data) => {
    if (!isAuthenticated) return;
    setSyncStatus('syncing');
    try {
      await client.put('/portfolios', { portfolios: data });
      setSyncStatus('synced');
      // keep silent in production
    } catch (error) {
      console.error('Failed to sync portfolios to database:', error);
      setSyncStatus('error');
    }
  }, [isAuthenticated]);

  // Auto-sync on changes (debounced)
  const triggerSync = useCallback((newData) => {
    // Always save to localStorage immediately
    localStorage.setItem('portfolioData', JSON.stringify(newData));

    // Also persist into user settings for cross-device sync
    try {
      schedulePersistClientSettings({
        localStorage: {
          portfolioData: newData,
        },
      }, 500);
    } catch {}
    
    // Debounce database sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    
    if (isAuthenticated) {
      syncTimeoutRef.current = setTimeout(() => {
        syncToDatabase(newData);
      }, 1000); // 1 second debounce
    }
  }, [isAuthenticated, syncToDatabase]);

  // Load portfolios: DB -> localStorage -> default
  useEffect(() => {
    const loadPortfolios = async () => {
      setIsLoading(true);
      if (isAuthenticated) {
        try {
          // Always fetch from database first
          const response = await client.get('/portfolios');
          const dbPortfolios = response.data;
          if (Array.isArray(dbPortfolios) && dbPortfolios.length > 0) {
            // Use database portfolios and update localStorage as backup
            setPortfolioLists(dbPortfolios);
            localStorage.setItem('portfolioData', JSON.stringify(dbPortfolios));
            setSyncStatus('synced');
          } else {
            // No DB data: try localStorage, else use default and upload to DB
            let next = null;
            try {
              const localData = localStorage.getItem('portfolioData');
              if (localData) {
                const parsed = JSON.parse(localData);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  next = parsed;
                }
              }
            } catch {}
            if (!next) {
              next = DEFAULT_PORTFOLIO;
            }
            // Upload to DB and set state
            // IMPORTANT: never overwrite existing localStorage portfolioData with DEFAULT_PORTFOLIO
            // if the DB call fails (e.g. 401). In that case, keep local data untouched.
            try {
              setSyncStatus('syncing');
              await client.put('/portfolios', { portfolios: next, only_if_empty: true });
              setSyncStatus('synced');
            } catch (e) {
              setSyncStatus('error');
              if (e?.response?.status === 401) {
                // Keep whatever is in localStorage; do not persist default.
                setPortfolioLists(next);
                return;
              }
            }
            setPortfolioLists(next);
            if (next !== DEFAULT_PORTFOLIO) {
              localStorage.setItem('portfolioData', JSON.stringify(next));
            }
          }
        } catch (error) {
          // Only fallback to localStorage if DB fetch fails
          const localData = localStorage.getItem('portfolioData');
          if (localData) {
            try {
              const parsed = JSON.parse(localData);
              setPortfolioLists(Array.isArray(parsed) ? parsed : DEFAULT_PORTFOLIO);
            } catch {
              setPortfolioLists(DEFAULT_EMPTY_LISTS);
            }
          } else {
            setPortfolioLists(DEFAULT_EMPTY_LISTS);
          }
          setSyncStatus('error');
        }
      } else {
        // Not authenticated - use localStorage only
        const localData = localStorage.getItem('portfolioData');
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            setPortfolioLists(Array.isArray(parsed) ? parsed : DEFAULT_PORTFOLIO);
          } catch {
            setPortfolioLists(DEFAULT_EMPTY_LISTS);
          }
        } else {
          setPortfolioLists(DEFAULT_EMPTY_LISTS);
        }
      }
      setIsLoading(false);
    };

    loadPortfolios();
  }, [isAuthenticated, syncToDatabase]);

  // Update expanded lists when portfolioLists changes
  useEffect(() => {
    setExpandedLists(prev => {
      const newExpanded = { ...prev };
      portfolioLists.forEach(list => {
        if (!(list.id in newExpanded)) {
          newExpanded[list.id] = true;
        }
      });
      return newExpanded;
    });
  }, [portfolioLists]);

  const openAddStockModal = (listId) => {
    setAddingToListId(listId);
    setEditingStock(null);
    setIsModalOpen(true);
  };

  const openRenameList = (listId) => {
    const current = portfolioLists.find(l => l.id === listId);
    setRenamingListId(listId);
    setRenameValue((current?.name || '').toString());
  };

  const closeRenameList = () => {
    setRenamingListId(null);
    setRenameValue('');
  };

  const saveRenameList = () => {
    const nextName = (renameValue || '').trim();
    if (!renamingListId) return;
    if (!nextName) {
      alert('Please enter a list name.');
      return;
    }
    setPortfolioLists(prevLists => {
      const newLists = prevLists.map(list => {
        if (list.id === renamingListId) {
          return { ...list, name: nextName };
        }
        return list;
      });
      triggerSync(newLists);
      return newLists;
    });
    closeRenameList();
  };

  const openEditStockModal = (listId, stock) => {
    setAddingToListId(listId);
    setEditingStock(stock);
    setIsModalOpen(true);
  };

  const closeAddStockModal = () => {
    setIsModalOpen(false);
    setAddingToListId(null);
    setEditingStock(null);
  };

  const handleContextMenu = (e, symbol, market) => {
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      symbol,
      market,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const toggleList = (listId) => {
    setExpandedLists(prev => ({ ...prev, [listId]: !prev[listId] }));
  };

  const tableCls = theme === 'dark' 
    ? 'min-w-full text-sm divide-y divide-gray-700'
    : 'min-w-full text-sm divide-y divide-gray-200';
  const thCls = 'px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ' + (theme === 'dark' ? 'text-gray-400' : 'text-gray-500');
  const tdCls = 'px-4 py-3 whitespace-nowrap text-center ' + (theme === 'dark' ? 'text-gray-200' : 'text-gray-800');

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return '-';
    return `$${value.toFixed(2)}`;
  };

  const formatChange = (value, percent) => {
    if (typeof value !== 'number' || typeof percent !== 'number') return '-';
    const color = value > 0 ? 'text-green-500' : value < 0 ? 'text-red-500' : 'text-gray-500';
    const sign = value > 0 ? '+' : '';
    return (
      <span className={color}>
        {sign}{formatCurrency(value)} ({sign}{percent.toFixed(2)}%)
      </span>
    );
  };

  const formatPL = (value) => {
    if (typeof value !== 'number') return '-';
    const color = value >= 0 ? 'text-green-500' : 'text-red-500';
    const sign = value >= 0 ? '+' : '-';
    return (
      <span className={color}>
        {sign}{formatCurrency(Math.abs(value))}
      </span>
    );
  };

  const handleDeleteStock = (listId, stockSymbol) => {
    setPortfolioLists(prevLists => {
      const newLists = prevLists.map(list => {
        if (list.id === listId) {
          return { ...list, stocks: list.stocks.filter(stock => stock.symbol !== stockSymbol) };
        }
        return list;
      });
      triggerSync(newLists);
      return newLists;
    });
  };

  const handleAddStock = async (stockData) => {
    try {
      const quoteResponse = await client.get(`/quote?symbol=${stockData.symbol}`);
      const quote = quoteResponse.data;

      const newStock = {
        id: `stock-${Date.now()}`,
        symbol: stockData.symbol,
        market: stockData.market || 'NASDAQ',
        ...(stockData.shares !== undefined && { shares: stockData.shares }),
        ...(stockData.avgBuyPrice !== undefined && { avgBuyPrice: stockData.avgBuyPrice }),
      };

      setPortfolioLists(prevLists => {
        const newLists = prevLists.map(list => {
          if (list.id === addingToListId) {
            if (list.stocks.find(s => s.symbol.toUpperCase() === newStock.symbol.toUpperCase())) {
              throw new Error(`${newStock.symbol} is already in this list.`);
            }
            // Always include type in the list object
            return { ...list, type: list.type, stocks: [...list.stocks, newStock] };
          }
          // Ensure type is always present
          return { ...list, type: list.type };
        });
        triggerSync(newLists);
        return newLists;
      });
    } catch (error) {
      console.error("Failed to add symbol:", error);
      throw error;
    }
  };

  const handleEditStock = async (stockData) => {
    setPortfolioLists(prevLists => {
      const nextSymbol = (stockData.symbol || '').toUpperCase();
      const originalSymbol = (stockData.originalSymbol || editingStock?.symbol || '').toUpperCase();
      if (!nextSymbol || !originalSymbol) return prevLists;

      const newLists = prevLists.map(list => {
        if (list.id === addingToListId) {
          const wouldDuplicate = list.stocks.some(
            (s) => (s.symbol || '').toUpperCase() === nextSymbol && (s.symbol || '').toUpperCase() !== originalSymbol
          );
          if (wouldDuplicate) {
            throw new Error(`${nextSymbol} is already in this list.`);
          }

          return {
            ...list,
            type: list.type,
            stocks: list.stocks.map(stock => {
              if ((stock.symbol || '').toUpperCase() === originalSymbol) {
                return {
                  ...stock,
                  symbol: nextSymbol,
                  shares: stockData.shares,
                  avgBuyPrice: stockData.avgBuyPrice,
                };
              }
              return stock;
            }),
          };
        }
        // Ensure type is always present
        return { ...list, type: list.type };
      });
      triggerSync(newLists);
      return newLists;
    });
  };

  const handleDeleteList = (listId) => {
    if (window.confirm('Are you sure you want to delete this entire list?')) {
      setPortfolioLists(prevLists => {
        const newLists = prevLists.filter(list => list.id !== listId);
        triggerSync(newLists);
        return newLists;
      });
    }
  };

  const handleAddNewList = () => {
    setIsAddListModalOpen(true);
  };

  const handleCreateList = (listName, listType) => {
    const newList = {
      id: `list-${Date.now()}`,
      name: listName,
      type: listType,
      stocks: [],
    };
    setPortfolioLists(prevLists => {
      // Ensure all lists have type and keep watchlists first.
      const newLists = [...prevLists.map(l => ({ ...l, type: l.type })), newList]
        .sort((a, b) => {
          const aIsWatch = a.type === 'watchlist';
          const bIsWatch = b.type === 'watchlist';
          if (aIsWatch === bIsWatch) return 0;
          return aIsWatch ? -1 : 1;
        });
      triggerSync(newLists);
      return newLists;
    });
    setExpandedLists(prev => ({ ...prev, [newList.id]: true }));
  };

  // Sync status indicator
  const getSyncIcon = () => {
    switch (syncStatus) {
      case 'syncing':
        return <CloudArrowUpIcon className="h-5 w-5 text-yellow-500 animate-pulse" title="Syncing..." />;
      case 'synced':
        return <CloudArrowDownIcon className="h-5 w-5 text-green-500" title="Synced to cloud" />;
      case 'error':
        return <CloudArrowUpIcon className="h-5 w-5 text-red-500" title="Sync failed" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-500">Loading portfolios...</div>
      </div>
    );
  }

  // Get the current list type for the modal
  const currentListType = portfolioLists.find(l => l.id === addingToListId)?.type || 'portfolio';

  return (
    <div className="space-y-8">
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          symbol={contextMenu.symbol}
          market={contextMenu.market}
          onClose={closeContextMenu}
        />
      )}
      
      <AddStockModal 
        isOpen={isModalOpen}
        onClose={closeAddStockModal}
        onAddStock={handleAddStock}
        onEditStock={handleEditStock}
        editingStock={editingStock}
        theme={theme}
        listType={currentListType}
      />

      <AddListModal
        isOpen={isAddListModalOpen}
        onClose={() => setIsAddListModalOpen(false)}
        onAddList={handleCreateList}
        theme={theme}
        defaultType="watchlist"
      />

      {renamingListId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          onClick={closeRenameList}
        >
          <div
            className={`p-6 rounded-lg shadow-xl w-full max-w-md ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Edit List Name</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveRenameList();
              }}
              className="space-y-4"
            >
              <div>
                <label htmlFor="renameListName" className="block text-sm font-medium mb-1">List Name</label>
                <input
                  id="renameListName"
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'border-gray-300 bg-white'}`}
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={closeRenameList}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Portfolio</h1>
          {isAuthenticated && getSyncIcon()}
        </div>
        <button
          onClick={handleAddNewList}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          <PlusCircleIcon className="h-5 w-5" />
          Add New List
        </button>
      </div>

      {portfolioLists.length === 0 ? (
        <div className={`p-8 text-center rounded-lg border ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <p className="text-gray-500 mb-4">You don't have any portfolio lists yet.</p>
          <button
            onClick={handleAddNewList}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Create Your First List
          </button>
        </div>
      ) : (
        portfolioLists.map(list => (
          <div key={list.id} className={`rounded-lg border ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold">{list.name}</h2>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${list.type === 'watchlist' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'}`}>
                  {list.type === 'watchlist' ? 'Watchlist' : 'Portfolio'}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={() => openAddStockModal(list.id)} className="flex items-center gap-1 text-xs hover:underline">
                  <PlusCircleIcon className="h-4 w-4" /> {'Add Symbol'}
                </button>
                <button
                  onClick={() => openRenameList(list.id)}
                  className="flex items-center gap-1 text-xs hover:underline"
                  title="Edit list name"
                >
                  <PencilIcon className="h-4 w-4" /> Rename
                </button>
                <button onClick={() => handleDeleteList(list.id)} className="flex items-center gap-1 text-xs text-red-500 hover:underline">
                  <TrashIcon className="h-4 w-4" /> Delete List
                </button>
                <button onClick={() => toggleList(list.id)} className="p-1 rounded-full hover:bg-gray-700">
                  {expandedLists[list.id] ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>
            {expandedLists[list.id] && (
              <div className="overflow-x-auto">
                {list.stocks.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No stocks in this list. Click "{'Add Symbol'}" to get started.
                  </div>
                ) : list.type === 'watchlist' ? (
                  <table className={tableCls}>
                    <thead className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}>
                      <tr>
                        <th className={thCls}>Symbol</th>
                        <th className={thCls}>Last Price</th>
                        <th className={thCls}>Change</th>
                        <th className={thCls}>3M Chart</th>
                        <th className="px-4 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {list.stocks.map(stock => (
                        <WatchlistStockRow
                          key={stock.id || stock.symbol}
                          stock={stock}
                          listId={list.id}
                          theme={theme}
                          tdCls={tdCls}
                          formatCurrency={formatCurrency}
                          formatChange={formatChange}
                          handleDeleteStock={handleDeleteStock}
                          navigate={navigate}
                          onContextMenu={handleContextMenu}
                        />
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className={tableCls}>
                    <thead className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}>
                      <tr>
                        <th className={thCls}>Symbol</th>
                        <th className={thCls}>Last Price</th>
                        <th className={thCls}>Change</th>
                        <th className={thCls}>Shares</th>
                        <th className={thCls}>Avg. Buy Price</th>
                        <th className={thCls}>Unrealized P&L</th>
                        <th className={thCls}>3M Chart</th>
                        <th className="px-4 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {list.stocks.map(stock => (
                        <PortfolioStockRow
                          key={stock.id || stock.symbol}
                          stock={stock}
                          listId={list.id}
                          theme={theme}
                          tdCls={tdCls}
                          formatCurrency={formatCurrency}
                          formatChange={formatChange}
                          formatPL={formatPL}
                          handleDeleteStock={handleDeleteStock}
                          handleEditStock={openEditStockModal}
                          navigate={navigate}
                          onContextMenu={handleContextMenu}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default Portfolio;
